import { ErrorLogComponent } from '@cian/error-log-component';
import { Microfrontend } from '@cian/mf-react';
import type { ICianMFUIElement } from '@cian/mf-registry/browser';
import { useCallback, useEffect, useRef } from 'react';

import { ECalculatorEvent } from 'shared/constants/calculatorEvents';
import { ETelemetryEventType } from 'shared/types/telemetry';
import { IWidgetManifest } from 'shared/types/widgetManifest';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { setCalculatorMicrofrontendElement } from 'shared/utils/calculatorMicrofrontend';

import { ERuntimeSettings } from '../../../types/runtimeSettings';

interface ICalculatorMicrofrontendProps {
  onSubmitCalculatorEvent(e: CustomEvent): void;
  onCatchErrorCalculatorEvent(e: CustomEvent): void;
  onSendAnalyticsCalculatorEvent(e: CustomEvent): void;
}

const detachCalculatorEventListeners = (
  element: ICianMFUIElement,
  handlers: {
    onSubmitCalculatorEvent: (e: CustomEvent) => void;
    onCatchErrorCalculatorEvent: (e: CustomEvent) => void;
    onSendAnalyticsCalculatorEvent: (e: CustomEvent) => void;
  },
) => {
  element.removeEventListener(ECalculatorEvent.onSubmit, handlers.onSubmitCalculatorEvent);
  element.removeEventListener(ECalculatorEvent.onError, handlers.onCatchErrorCalculatorEvent);
  element.removeEventListener(ECalculatorEvent.onAnalytics, handlers.onSendAnalyticsCalculatorEvent);
};

const attachCalculatorEventListeners = (
  element: ICianMFUIElement,
  handlers: {
    onSubmitCalculatorEvent: (e: CustomEvent) => void;
    onCatchErrorCalculatorEvent: (e: CustomEvent) => void;
    onSendAnalyticsCalculatorEvent: (e: CustomEvent) => void;
  },
) => {
  element.addEventListener(ECalculatorEvent.onSubmit, handlers.onSubmitCalculatorEvent);
  element.addEventListener(ECalculatorEvent.onError, handlers.onCatchErrorCalculatorEvent);
  element.addEventListener(ECalculatorEvent.onAnalytics, handlers.onSendAnalyticsCalculatorEvent);
};

/* istanbul ignore next */
export const CalculatorMicrofrontend = ({
  onSubmitCalculatorEvent,
  onCatchErrorCalculatorEvent,
  onSendAnalyticsCalculatorEvent,
}: ICalculatorMicrofrontendProps) => {
  const { config, logger, telemetry } = useApplicationContext();
  const mcfRef = useRef<ICianMFUIElement>(null);
  const manifest = config.get<IWidgetManifest>(ERuntimeSettings.offerCalculatorManifest);
  const handlersRef = useRef({
    onSubmitCalculatorEvent,
    onCatchErrorCalculatorEvent,
    onSendAnalyticsCalculatorEvent,
  });

  handlersRef.current = {
    onSubmitCalculatorEvent,
    onCatchErrorCalculatorEvent,
    onSendAnalyticsCalculatorEvent,
  };

  const setMcfRef = useCallback((node: ICianMFUIElement | null) => {
    const prev = mcfRef.current;

    if (prev) {
      detachCalculatorEventListeners(prev, handlersRef.current);
    }

    mcfRef.current = node;
    setCalculatorMicrofrontendElement(node);

    if (node) {
      attachCalculatorEventListeners(node, handlersRef.current);
    }
  }, []);

  useEffect(() => {
    const node = mcfRef.current;

    if (node) {
      detachCalculatorEventListeners(node, handlersRef.current);
      attachCalculatorEventListeners(node, handlersRef.current);
    }
  }, [onCatchErrorCalculatorEvent, onSendAnalyticsCalculatorEvent, onSubmitCalculatorEvent]);

  useEffect(() => {
    return () => {
      const current = mcfRef.current;

      if (current) {
        detachCalculatorEventListeners(current, handlersRef.current);
      }

      setCalculatorMicrofrontendElement(null);
    };
  }, []);

  useEffect(() => {
    if (!manifest) {
      telemetry.increment(ETelemetryEventType.OfferCalculatorRenderError);
    }
  }, [manifest, telemetry]);

  if (!manifest) {
    logger.error('Отсутствует манифест микрофронта Ипотечного Калькулятора offer-calculator', {
      domain: 'microfrontends.offer-calculator.manifest',
    });

    return null;
  }

  return (
    <ErrorLogComponent
      logger={logger}
      onError={() => {
        telemetry.increment(ETelemetryEventType.OfferCalculatorRenderError);
      }}
    >
      <Microfrontend {...manifest} ref={setMcfRef} />
    </ErrorLogComponent>
  );
};
